import React, { useState, useEffect, useRef, useContext } from 'react'
import { Container } from './Container'
import { Button } from './Buttons'
import DataFlowDiagram from './DataFlowDiagram'
import { ChatSupportContext } from '../../contexts/ChatSupportContext'

function CheckIcon(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        d="M9.307 12.248a.75.75 0 1 0-1.114 1.004l1.114-1.004ZM11 15.25l-.557.502a.75.75 0 0 0 1.15-.043L11 15.25Zm7.593-5.422a.75.75 0 0 0-.977-1.143l.977 1.143ZM9.307 14.252l1.693 1.875 1.15-.043-2.25-2.835-1.114 1.004Zm3.731-.75-6.5-7.208-.977 1.143 6.5 7.208.977-1.143Z"
        fill="currentColor"
      />
    </svg>
  )
}

function SwapIcon({ className, ...props }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        d="M12 1.5a.75.75 0 0 1 .75.75V4.5a.75.75 0 0 1-1.5 0V2.25A.75.75 0 0 1 12 1.5ZM5.636 4.136a.75.75 0 0 1 1.06 0l1.592 1.591a.75.75 0 0 1-1.061 1.06l-1.591-1.59a.75.75 0 0 1 0-1.061Zm12.728 0a.75.75 0 0 1 0 1.06l-1.591 1.592a.75.75 0 0 1-1.06-1.061l1.59-1.591a.75.75 0 0 1 1.061 0Zm-6.816 4.496a.75.75 0 0 1 .82.311l5.228 7.917a.75.75 0 0 1-.777 1.148l-2.097-.43 1.045 3.9a.75.75 0 0 1-1.45.388l-1.044-3.899-1.601 1.42a.75.75 0 0 1-1.247-.606l.569-9.47a.75.75 0 0 1 .554-.68ZM3 10.5a.75.75 0 0 1 .75-.75H6a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 10.5Zm14.25 0a.75.75 0 0 1 .75-.75h2.25a.75.75 0 0 1 0 1.5H18a.75.75 0 0 1-.75-.75Zm-8.962 3.712a.75.75 0 0 1 0 1.061l-1.591 1.591a.75.75 0 1 1-1.061-1.06l1.591-1.592a.75.75 0 0 1 1.06 0Zm10.021 0a.75.75 0 0 1 0 1.061l-1.591 1.591a.75.75 0 1 1-1.061-1.06l1.591-1.592a.75.75 0 0 1 1.06 0ZM12 18a.75.75 0 0 1 .75.75V21a.75.75 0 0 1-1.5 0v-2.25A.75.75 0 0 1 12 18Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

const plans = [
  {
    name: 'Starter',
    featured: false,
    price: { Monthly: '$499', Annual: '$399' },
    description:
      'Essential compliance tools for small organizations starting their SOX journey.',
    features: [
      'Automated SOX Testing (up to 50 controls)',
      'Basic Real-Time Dashboards',
      'Access Request Management',
      'User Access Review (quarterly)',
      'Standard Email Support',
      'Up to 3 users'
    ],
    cta: 'Start your trial',
    ctaVariant: 'secondary',
  },
  {
    name: 'Professional',
    featured: true,
    price: { Monthly: '$999', Annual: '$799' },
    description:
      'Complete compliance automation for growing organizations with complex needs.',
    features: [
      'Automated SOX Testing (unlimited controls)',
      'Advanced Real-Time Dashboards',
      'Access Request Management with custom workflows',
      'User Access Review (monthly)',
      'Segregation of Duties Analysis',
      'API Integration with Major ERPs',
      'Priority Support',
      'Up to 15 users'
    ],
    cta: 'Start your trial',
    ctaVariant: 'primary',
  },
  {
    name: 'Enterprise',
    featured: false,
    price: { Monthly: 'Custom', Annual: 'Custom' },
    description:
      'Advanced compliance solutions for large enterprises with sophisticated requirements.',
    features: [
      'All Professional features',
      'Custom Control Framework Integration',
      'Multi-ERP Support',
      'Advanced Analytics and Risk Scoring',
      'Custom Reporting',
      'Dedicated Account Manager',
      'Priority Support with SLA',
      'Unlimited users',
      'On-premises deployment option',
    ],
    cta: 'Contact sales',
    ctaVariant: 'secondary',
  },
]

// Integration partners with logos and descriptions
const integrationPartners = [
  {
    name: 'ERP Systems',
    description: 'Seamlessly connect to your enterprise resource planning systems to extract user access data, transaction logs, and configuration settings for comprehensive compliance monitoring.',
    category: 'Core Systems',
    integrations: [
      { name: 'SAP', logo: '/img/integrations/sap.svg' },
      { name: 'Oracle', logo: '/img/integrations/oracle.svg' },
      { name: 'Microsoft Dynamics', logo: '/img/integrations/dynamics.svg' },
      { name: 'NetSuite', logo: '/img/integrations/netsuite.svg' },
    ]
  },
  {
    name: 'Identity Management',
    description: 'Integrate with identity providers to automate user access reviews, enforce segregation of duties, and maintain comprehensive access control across your organization.',
    category: 'Security',
    integrations: [
      { name: 'Okta', logo: '/img/integrations/okta.svg' },
      { name: 'Azure AD', logo: '/img/integrations/azure.svg' },
      { name: 'Ping Identity', logo: '/img/integrations/ping.svg' },
      { name: 'ForgeRock', logo: '/img/integrations/forgerock.svg' },
    ]
  },
  {
    name: 'Cloud Platforms',
    description: 'Monitor and enforce compliance across your cloud infrastructure with direct connections to leading providers, ensuring consistent controls in hybrid environments.',
    category: 'Infrastructure',
    integrations: [
      { name: 'AWS', logo: '/img/integrations/aws.svg' },
      { name: 'Google Cloud', logo: '/img/integrations/gcloud.svg' },
      { name: 'Microsoft Azure', logo: '/img/integrations/azure.svg' },
      { name: 'IBM Cloud', logo: '/img/integrations/ibm.svg' },
    ]
  },
  {
    name: 'Compliance Tools',
    description: 'Connect with specialized compliance and security tools to centralize your governance activities and provide a unified view of your compliance posture.',
    category: 'Governance',
    integrations: [
      { name: 'ServiceNow', logo: '/img/integrations/servicenow.svg' },
      { name: 'Jira', logo: '/img/integrations/jira.svg' },
      { name: 'Archer', logo: '/img/integrations/archer.svg' },
      { name: 'MetricStream', logo: '/img/integrations/metricstream.svg' },
    ]
  },
  {
    name: 'Analytics Platforms',
    description: 'Export compliance data to analytics platforms for advanced reporting, visualization, and insights that help you identify patterns and optimize your control environment.',
    category: 'Intelligence',
    integrations: [
      { name: 'Tableau', logo: '/img/integrations/tableau.svg' },
      { name: 'Power BI', logo: '/img/integrations/powerbi.svg' },
      { name: 'Looker', logo: '/img/integrations/looker.svg' },
      { name: 'Qlik', logo: '/img/integrations/qlik.svg' },
    ]
  },
  {
    name: 'Custom Systems',
    description: 'Connect to any custom or legacy application with our flexible API ecosystem and custom integration services, ensuring complete coverage of your technology landscape.',
    category: 'Extensibility',
    integrations: [
      { name: 'REST API', logo: '/img/integrations/api.svg' },
      { name: 'Database Connectors', logo: '/img/integrations/database.svg' },
      { name: 'File Imports', logo: '/img/integrations/file.svg' },
      { name: 'Custom Adapters', logo: '/img/integrations/custom.svg' },
    ]
  },
]

// Integration categories with icons and colors
const integrationCategories = [
  {
    name: 'All Categories',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
      </svg>
    ),
    color: 'from-blue-600 to-indigo-600'
  },
  {
    name: 'Core Systems',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
      </svg>
    ),
    color: 'from-green-600 to-emerald-600'
  },
  {
    name: 'Security',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
    color: 'from-red-600 to-rose-600'
  },
  {
    name: 'Infrastructure',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.737 5.1a3.375 3.375 0 0 1 2.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 0 1 .9 2.7m0 0a3 3 0 0 1-3 3m0 3h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Zm-3 6h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Z" />
      </svg>
    ),
    color: 'from-blue-600 to-cyan-600'
  },
  {
    name: 'Governance',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" />
      </svg>
    ),
    color: 'from-amber-500 to-orange-600'
  },
  {
    name: 'Intelligence',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.75-9 3-3 2.148 2.148A12.061 12.061 0 0 1 16.5 7.605" />
      </svg>
    ),
    color: 'from-purple-600 to-indigo-600'
  },
  {
    name: 'Extensibility',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 9.75 16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
      </svg>
    ),
    color: 'from-gray-600 to-slate-800'
  },
]

// Placeholder component for integration logos
function IntegrationLogo({ name, logo }) {
  return (
    <div className="flex flex-col items-center justify-center p-2">
      <div className="h-10 w-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
        <svg 
          className="w-6 h-6 text-gray-400" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <rect width="18" height="18" x="3" y="3" rx="2" ry="2" strokeWidth="1.5" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 7v10m-5-5h10" />
        </svg>
      </div>
      <span className="mt-1 text-xs text-gray-600">{name}</span>
    </div>
  );
}

export function TechnologyIntegrations() {
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  
  // Get the chat support ref from context
  const { chatSupportRef } = useContext(ChatSupportContext);
  
  // Filter integrations based on selected category
  const filteredIntegrations = selectedCategory === 'All Categories' 
    ? integrationPartners 
    : integrationPartners.filter(partner => partner.category === selectedCategory);
  
  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.75) {
          setIsVisible(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    // Trigger once on mount
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle the contact us button click
  const handleContactClick = (e) => {
    e.preventDefault();
    // Open the chat without specifying a category
    if (chatSupportRef && chatSupportRef.current) {
      chatSupportRef.current.openChat();
    }
  };

  return (
    <section
      id="integrations"
      aria-label="Technology Integrations"
      className="py-20 sm:py-32 bg-white relative overflow-hidden"
      ref={sectionRef}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Premium glass-like gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/30"></div>
        
        {/* Hexagon pattern for tech feel */}
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="hexagons" width="50" height="43.4" patternUnits="userSpaceOnUse" patternTransform="scale(2)">
                <path d="M25 0L50 14.4v28.8L25 43.4L0 28.8V14.4z" strokeWidth="1" stroke="currentColor" fill="none" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hexagons)" />
          </svg>
        </div>
        
        {/* Circuit-like connection lines */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="circuit-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(37, 99, 235, 0.5)" />
                <stop offset="100%" stopColor="rgba(79, 70, 229, 0.5)" />
              </linearGradient>
            </defs>
            <g stroke="url(#circuit-gradient)" fill="none" strokeWidth="0.5">
              <path d="M0,50 L1000,50" />
              <path d="M0,150 L1000,150" />
              <path d="M0,250 L1000,250" />
              <path d="M0,350 L1000,350" />
              <path d="M0,450 L1000,450" />
              <path d="M100,0 L100,1000" />
              <path d="M200,0 L200,1000" />
              <path d="M300,0 L300,1000" />
              <path d="M400,0 L400,1000" />
              <path d="M500,0 L500,1000" />
              <path d="M600,0 L600,1000" />
              <path d="M700,0 L700,1000" />
              <path d="M800,0 L800,1000" />
              <path d="M900,0 L900,1000" />
              <circle cx="100" cy="150" r="5" />
              <circle cx="300" cy="250" r="5" />
              <circle cx="500" cy="50" r="5" />
              <circle cx="700" cy="350" r="5" />
              <circle cx="900" cy="250" r="5" />
              <circle cx="200" cy="450" r="5" />
              <circle cx="600" cy="150" r="5" />
              <circle cx="400" cy="350" r="5" />
              <circle cx="800" cy="450" r="5" />
            </g>
          </svg>
        </div>
        
        {/* Flowing data nodes - subtle animation */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 rounded-full bg-blue-600 animate-pulse"></div>
          <div className="absolute top-3/4 left-1/2 w-2 h-2 rounded-full bg-indigo-600 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/3 right-1/4 w-2 h-2 rounded-full bg-purple-600 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute top-2/3 right-1/3 w-2 h-2 rounded-full bg-blue-600 animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        </div>
      </div>
      
      <Container>
        <div className="mx-auto max-w-3xl text-center fade-in-up visible">
          <div className="inline-flex items-center justify-center gap-2 mb-4">
            <div className="h-px w-8 bg-blue-600/30"></div>
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest">Enterprise Connectivity</p>
            <div className="h-px w-8 bg-blue-600/30"></div>
          </div>
          
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl lg:text-5xl mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-800 font-extrabold">Seamless Integration Ecosystem</span>
          </h2>
          
          <p className="mt-4 text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
            AUDIT-AI connects with all your critical systems to automate compliance workflows and deliver unprecedented insights across your enterprise landscape.
          </p>
          
       
         
        </div>

        <DataFlowDiagram isMobile={false} />

      
        
        {/* Integration CTA - Premium Design */}
        <div className="mt-20 relative fade-in-up visible">
          {/* Decorative elements */}
          <div className="absolute -top-12 -left-12 w-24 h-24 rounded-full bg-blue-500/10 blur-2xl"></div>
          <div className="absolute -bottom-12 -right-12 w-32 h-32 rounded-full bg-indigo-500/10 blur-3xl"></div>
          
          {/* Main CTA container */}
          <div className="relative rounded-2xl overflow-hidden">
            {/* Glass background with gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 via-indigo-600/90 to-blue-800/90 backdrop-blur-sm"></div>
            
            {/* Geometric decorations */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {/* Abstract shapes */}
              <svg className="absolute top-0 right-0 h-64 w-64 text-white/5" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                <path fill="currentColor" d="M42.7,-62.2C53.8,-52.4,60.3,-37.8,64.6,-23.1C68.9,-8.5,71,-6.1,69.4,13.7C67.8,33.6,62.3,61,47.6,72.1C32.8,83.3,8.7,78.3,-12.1,71.8C-32.9,65.4,-50.4,57.6,-61.4,44.2C-72.5,30.8,-77.2,11.8,-73.6,-4.3C-70,-20.4,-58.2,-33.6,-45.3,-42.9C-32.3,-52.3,-18.2,-57.8,-1.8,-55.7C14.6,-53.6,31.6,-72,42.7,-62.2Z" transform="translate(100 100)" />
              </svg>
              
              <svg className="absolute bottom-0 left-0 h-64 w-64 text-white/5" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                <path fill="currentColor" d="M47.7,-73.2C59.3,-62.3,64.6,-44.1,68.5,-27.2C72.5,-10.3,75.1,5.3,71.6,19.8C68.1,34.2,58.5,47.5,45.7,56.6C32.8,65.6,16.4,70.6,-0.9,71.9C-18.2,73.3,-36.4,71,-49.4,61.4C-62.3,51.8,-70,34.9,-73.8,16.9C-77.5,-1.1,-77.2,-20.2,-69.5,-35.5C-61.8,-50.8,-46.8,-62.4,-32,-70.1C-17.1,-77.8,-2.4,-81.7,12.4,-79.3C27.1,-77,36.1,-84.1,47.7,-73.2Z" transform="translate(100 100)" />
              </svg>
              
              {/* Animated pulse rings */}
              <div className="absolute top-1/4 right-1/4">
                <div className="w-32 h-32 border-2 border-white/10 rounded-full animate-ping" style={{ animationDuration: '3s' }}></div>
              </div>
              <div className="absolute bottom-1/4 left-1/3">
                <div className="w-24 h-24 border-2 border-white/10 rounded-full animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
              </div>
              
              {/* Connection lines */}
              <div className="absolute inset-0">
                <svg width="100%" height="100%" className="opacity-10">
                  <defs>
                    <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M0 0 L40 40 M40 0 L0 40" stroke="white" strokeWidth="0.5" fill="none" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid-pattern)" />
                </svg>
              </div>
            </div>
            
            {/* Content */}
            <div className="relative px-6 py-12 sm:px-12 md:py-16 md:px-16">
              <div className="max-w-4xl mx-auto text-center">
                {/* Eyebrow */}
                <div className="inline-flex items-center justify-center mb-3 bg-white/10 backdrop-blur-sm px-4 py-1 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-100">Enterprise-Ready Integration</span>
                </div>
                
                {/* Heading with premium styling */}
                <h3 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                  Discover How <span className="relative">
                    <span className="relative z-10">AUDIT-AI</span>
                    <span className="absolute bottom-1 left-0 w-full h-3 bg-blue-400/20 rounded-sm -z-0"></span>
                  </span> Connects Your Systems
                </h3>
                
                {/* Description with highlighted text */}
                <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Our integration platform enables seamless data flow between your critical systems, 
                  creating a <span className="text-white font-medium">unified compliance environment</span> that 
                  delivers real-time insights and automates governance processes.
                </p>
                
                {/* CTA Buttons with premium styling */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
                  <a 
                    href="#get-started" 
                    className="px-8 py-3 bg-white text-blue-700 font-semibold rounded-lg shadow-lg hover:bg-blue-50 transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                    Schedule a Demo
                  </a>
                  <a 
                    href="#contact"
                    onClick={handleContactClick}
                    className="px-8 py-3 bg-transparent text-white font-semibold rounded-lg border border-white/30 backdrop-blur-sm hover:bg-white/10 transition-all duration-300 flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
} 

// Export renamed component for backward compatibility
export const Pricing = TechnologyIntegrations;

// Export the component with a more appropriate name
export { TechnologyIntegrations as Integrations };
export default TechnologyIntegrations; 